import { ReactComponent as VoucherAvailable } from 'assets/icons/voucher-available.svg';
import { ReactComponent as VoucherUnavailable } from 'assets/icons/voucher-unavailable.svg';
import { useCallback, useContext, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Tooltip } from 'antd';
import Button from 'components/button';
import EnterpriseUpsell, {
  CERTIFICATION_INSIGHTS_UPSELL_COPY,
  ENTERPRISE_UPSELL_FEATURES,
  ENTERPRISE_UPSELL_METRICS,
  HighlightedText,
} from 'components/enterpriseUpsell';
import { CertificationContext } from 'contexts/certifications';
import { SessionContext } from 'contexts/session';
import { buildContactSalesMailto } from 'utils/contactSales';
import AssignVoucherModal from '../AssignVoucherModal';
import {
  VoucherActions as AvailableVoucherActions,
  VoucherInfo as AvailableVoucherInfo,
  ContactSalesButton,
  NeedMoreVouchers,
  NoVoucherActions,
  VoucherDetails,
  VoucherLabel,
  VoucherQuantity,
} from '../CertificationsCounters/styles';
import { Header, Title } from '../styles';
import {
  FomoWrapper,
  RestrictedNoVoucherCard,
  RestrictedVoucherCard,
  RestrictedWrapper,
} from './styles';

const CertificationsRestricted = (): JSX.Element => {
  const { voucherSummary } = useContext(CertificationContext);
  const { account, isPastDue } = useContext(SessionContext);
  const voucherAssignmentV1 = useFlags()?.voucherAssignmentV1 ?? false;
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const remaining = voucherSummary?.voucher_balance?.remaining ?? 0;
  const hasVouchers = remaining > 0;

  const handleContactSales = useCallback(() => {
    const subject = encodeURIComponent('Request for Additional Certification Vouchers');
    const body = encodeURIComponent(
      'Hi,\n\nI would like to request additional certification vouchers for my account.\n\nThank you'
    );
    window.location.href = buildContactSalesMailto(account, { subject, body });
  }, [account]);

  const handleAssignVoucher = useCallback(() => {
    setAssignModalOpen(true);
  }, []);

  const CurrentVoucherActions = hasVouchers ? AvailableVoucherActions : NoVoucherActions;
  const CurrentVoucherCard = hasVouchers ? RestrictedVoucherCard : RestrictedNoVoucherCard;

  return (
    <RestrictedWrapper>
      <Header>
        <Title>Certifications</Title>
      </Header>

      {voucherAssignmentV1 && (
        <CurrentVoucherCard>
          <AvailableVoucherInfo>
            {hasVouchers ? (
              <VoucherAvailable
                data-testid="voucher-available-icon"
                aria-hidden="true"
                focusable="false"
              />
            ) : (
              <VoucherUnavailable
                data-testid="voucher-unavailable-icon"
                aria-hidden="true"
                focusable="false"
              />
            )}
            <VoucherDetails>
              <VoucherQuantity>{remaining}</VoucherQuantity>
              <VoucherLabel>Vouchers available</VoucherLabel>
            </VoucherDetails>
          </AvailableVoucherInfo>
          <CurrentVoucherActions>
            {hasVouchers ? (
              <>
                {isPastDue ? (
                  <Tooltip
                    title="Your account is no longer active. Please contact us to renew your plan and reinstate voucher assignment access."
                    placement="top"
                  >
                    <span>
                      <Button $primary size="small" disabled>
                        Assign vouchers
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button $primary size="small" onClick={handleAssignVoucher}>
                    Assign vouchers
                  </Button>
                )}
                <NeedMoreVouchers>
                  {'Need more vouchers?'}
                  <ContactSalesButton
                    onClick={handleContactSales}
                    data-testid="contact-sales-link"
                    aria-label="Contact sales to purchase more vouchers"
                    type="button"
                  >
                    Contact sales.
                  </ContactSalesButton>
                </NeedMoreVouchers>
              </>
            ) : (
              <Button $primary size="small" onClick={handleContactSales}>
                Contact sales to add vouchers
              </Button>
            )}
          </CurrentVoucherActions>
        </CurrentVoucherCard>
      )}

      <FomoWrapper>
        <EnterpriseUpsell
          eyebrow={CERTIFICATION_INSIGHTS_UPSELL_COPY.eyebrow}
          headline={
            <>
              Unlock{' '}
              <HighlightedText>
                {CERTIFICATION_INSIGHTS_UPSELL_COPY.headlineHighlight}
              </HighlightedText>{' '}
              with Enterprise
            </>
          }
          features={ENTERPRISE_UPSELL_FEATURES}
          metrics={ENTERPRISE_UPSELL_METRICS}
        />
      </FomoWrapper>

      {voucherAssignmentV1 && (
        <AssignVoucherModal
          visible={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          handleContactSales={handleContactSales}
        />
      )}
    </RestrictedWrapper>
  );
};

export default CertificationsRestricted;
